/**
 * Speech-to-text service for web browsers
 * Uses Web Speech API with fallback to Azure Speech Service WebSocket
 */

export interface SpeechRecognitionResult {
  text: string;
  confidence?: number;
  isFinal: boolean;
  timestamp: number;
}

export interface SpeechServiceConfig {
  baseUrl: string;
  token: string;
  language?: string;
  useWebSpeechAPI?: boolean;
}

export type SpeechEventHandler = (result: SpeechRecognitionResult) => void;
export type SpeechErrorHandler = (error: string) => void;
export type SpeechStatusHandler = (status: 'connecting' | 'connected' | 'recording' | 'stopped' | 'error') => void;

// Declare Web Speech API types
declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: any) => any) | null;
  onresult: ((this: SpeechRecognition, ev: any) => any) | null;
}

declare var SpeechRecognition: {
  prototype: SpeechRecognition;
  new(): SpeechRecognition;
};

class WebSpeechService {
  private recognition: SpeechRecognition | null = null;
  private ws: WebSocket | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private isRecording = false;
  private isConnecting = false;
  private useWebSpeechAPI = false;
  
  private onResult: SpeechEventHandler | null = null;
  private onError: SpeechErrorHandler | null = null;
  private onStatusChange: SpeechStatusHandler | null = null;
  
  private audioChunkInterval: number | null = null;
  private config: SpeechServiceConfig | null = null;

  /**
   * Initialize the speech service
   */
  async initialize(config: SpeechServiceConfig) {
    this.config = config;
    this.useWebSpeechAPI = config.useWebSpeechAPI !== false; // Default to true

    // Check for Web Speech API support
    if (this.useWebSpeechAPI && this.isWebSpeechAPISupported()) {
      console.log('Using Web Speech API');
      this.initializeWebSpeechAPI();
    } else {
      console.log('Using Azure Speech Service WebSocket');
      // Request microphone permissions for WebSocket approach
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        throw new Error(`Microphone permission denied: ${error}`);
      }
    }
  }

  /**
   * Check if Web Speech API is supported
   */
  private isWebSpeechAPISupported(): boolean {
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  }

  /**
   * Initialize Web Speech API
   */
  private initializeWebSpeechAPI() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      throw new Error('Web Speech API not supported');
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = this.config?.language || 'en-US';

    this.recognition.onstart = () => {
      console.log('Speech recognition started');
      this.onStatusChange?.('recording');
    };

    this.recognition.onresult = (event: any) => {
      const results = event.results;
      
      for (let i = event.resultIndex; i < results.length; i++) {
        const result = results[i];
        const transcript = result[0].transcript;
        const confidence = result[0].confidence;
        const isFinal = result.isFinal;

        this.onResult?.({
          text: transcript,
          confidence: confidence,
          isFinal: isFinal,
          timestamp: Date.now()
        });
      }
    };

    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      this.onStatusChange?.('error');
      this.onError?.(`Speech recognition error: ${event.error}`);
    };

    this.recognition.onend = () => {
      console.log('Speech recognition ended');
      this.isRecording = false;
      this.onStatusChange?.('stopped');
    };
  }

  /**
   * Set event handlers
   */
  setEventHandlers(
    onResult: SpeechEventHandler,
    onError: SpeechErrorHandler,
    onStatusChange: SpeechStatusHandler
  ) {
    this.onResult = onResult;
    this.onError = onError;
    this.onStatusChange = onStatusChange;
  }

  /**
   * Connect to the Azure Speech Service WebSocket
   */
  private async connectWebSocket(): Promise<void> {
    if (!this.config) {
      throw new Error('Speech service not initialized');
    }

    return new Promise((resolve, reject) => {
      this.isConnecting = true;
      this.onStatusChange?.('connecting');

      const wsUrl = `${this.config!.baseUrl.replace('http', 'ws')}/api/v1/speech/stream?token=${encodeURIComponent(this.config!.token)}`;
      
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('Azure Speech WebSocket connected');
        this.isConnecting = false;
        this.onStatusChange?.('connected');
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleWebSocketMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
          this.onError?.('Failed to parse server message');
        }
      };

      this.ws.onerror = (error) => {
        console.error('Azure Speech WebSocket error:', error);
        this.isConnecting = false;
        this.onStatusChange?.('error');
        this.onError?.('Connection error');
        reject(error);
      };

      this.ws.onclose = (event) => {
        console.log('Azure Speech WebSocket closed:', event.code, event.reason);
        this.isConnecting = false;
        this.cleanup();
        
        if (event.code !== 1000) {
          this.onStatusChange?.('error');
          this.onError?.(`Connection closed unexpectedly: ${event.reason}`);
        } else {
          this.onStatusChange?.('stopped');
        }
      };

      // Connection timeout
      setTimeout(() => {
        if (this.isConnecting) {
          this.isConnecting = false;
          this.ws?.close();
          reject(new Error('Connection timeout'));
        }
      }, 10000);
    });
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleWebSocketMessage(data: any) {
    switch (data.type) {
      case 'ready':
        console.log('Azure Speech service ready');
        break;

      case 'recognition_started':
        console.log('Recognition started');
        this.onStatusChange?.('recording');
        break;

      case 'recognizing':
        // Interim results
        this.onResult?.({
          text: data.text || '',
          confidence: data.confidence,
          isFinal: false,
          timestamp: data.timestamp || Date.now()
        });
        break;

      case 'recognized':
        // Final results
        this.onResult?.({
          text: data.text || '',
          confidence: data.confidence,
          isFinal: true,
          timestamp: data.timestamp || Date.now()
        });
        break;

      case 'recognition_stopped':
        console.log('Recognition stopped');
        this.onStatusChange?.('stopped');
        break;

      case 'error':
        console.error('Azure Speech service error:', data.message);
        this.onError?.(`Speech service error: ${data.message}`);
        break;

      default:
        console.warn('Unknown message type:', data.type);
    }
  }

  /**
   * Start speech recognition
   */
  async startRecording(): Promise<void> {
    if (this.isRecording) {
      return;
    }

    if (!this.config) {
      throw new Error('Speech service not initialized');
    }

    this.isRecording = true;

    try {
      if (this.useWebSpeechAPI && this.recognition) {
        // Use Web Speech API
        this.recognition.start();
      } else {
        // Use Azure Speech Service WebSocket
        await this.startAzureSpeechRecording();
      }
    } catch (error) {
      this.isRecording = false;
      console.error('Failed to start recording:', error);
      this.onError?.(`Failed to start recording: ${error}`);
      throw error;
    }
  }

  /**
   * Start Azure Speech Service recording
   */
  private async startAzureSpeechRecording() {
    // Connect to WebSocket if not already connected
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      await this.connectWebSocket();
    }

    // Start media recorder
    if (this.mediaStream) {
      this.mediaRecorder = new MediaRecorder(this.mediaStream, {
        mimeType: 'audio/wav; codecs=pcm'
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0 && this.ws && this.ws.readyState === WebSocket.OPEN) {
          // Convert audio blob to base64 and send to server
          const reader = new FileReader();
          reader.onload = () => {
            const arrayBuffer = reader.result as ArrayBuffer;
            const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
            
            this.ws?.send(JSON.stringify({
              type: 'audio_data',
              data: base64
            }));
          };
          reader.readAsArrayBuffer(event.data);
        }
      };

      this.mediaRecorder.start(250); // Send chunks every 250ms
    }

    // Start recognition on server
    this.ws?.send(JSON.stringify({
      type: 'start_recognition',
      options: {
        language: this.config?.language || 'en-US'
      }
    }));
  }

  /**
   * Stop speech recognition
   */
  async stopRecording(): Promise<void> {
    if (!this.isRecording) {
      return;
    }

    try {
      if (this.useWebSpeechAPI && this.recognition) {
        // Use Web Speech API
        this.recognition.stop();
      } else {
        // Use Azure Speech Service
        this.stopAzureSpeechRecording();
      }
    } catch (error) {
      console.error('Failed to stop recording:', error);
      this.onError?.(`Failed to stop recording: ${error}`);
    }

    this.isRecording = false;
  }

  /**
   * Stop Azure Speech Service recording
   */
  private stopAzureSpeechRecording() {
    // Stop media recorder
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    // Stop recognition on server
    this.ws?.send(JSON.stringify({
      type: 'stop_recognition'
    }));
  }

  /**
   * Check if service is currently recording
   */
  isCurrentlyRecording(): boolean {
    return this.isRecording;
  }

  /**
   * Check if service is available
   */
  async isAvailable(): Promise<boolean> {
    if (!this.config) {
      return false;
    }

    if (this.useWebSpeechAPI) {
      return this.isWebSpeechAPISupported();
    } else {
      // Check microphone permissions
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch {
        return false;
      }
    }
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    this.cleanup();
    this.onStatusChange?.('stopped');
  }

  /**
   * Internal cleanup
   */
  private cleanup() {
    if (this.audioChunkInterval) {
      clearInterval(this.audioChunkInterval);
      this.audioChunkInterval = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.recognition) {
      this.recognition.stop();
    }

    this.isRecording = false;
    this.isConnecting = false;
  }
}


export default new WebSpeechService();